import { useState } from "react";
import type { ReactNode } from "react";
import { IncidentNotificationState } from "@no-busy-work/ops-bridge-protocol";
import {
  CUSTOMER_COHORT_FILTER,
  CUSTOMER_FILTER_ALL,
  UI_TONE
} from "../OpsCenter.Config";
import { routeLabel, serviceLabel } from "../OpsCenter.Data";
import type { IncidentDetail, OpsCenterCopy, UiTone } from "../OpsCenter.Types";
import { humanize, statusLabel } from "../i18n/OpsCenter.I18n";
import { Metric, SectionHeader, SelectField, StatusPill, TextField, formatLabel } from "./OpsCenterScreen.Shared";

const CUSTOMER_DRILLDOWN_LIMIT = 25;

type CustomerCohortFilter = (typeof CUSTOMER_COHORT_FILTER)[keyof typeof CUSTOMER_COHORT_FILTER];
type CustomerImpactRow = IncidentDetail["customerImpact"][number];

interface CustomerDrilldownContext {
  readonly complaintRefs: ReadonlySet<string>;
  readonly customerNoticeState: IncidentNotificationState;
  readonly resolutionStateByCustomer: ReadonlyMap<string, IncidentNotificationState>;
}

export function CustomerImpactDrilldown({ detail, copy }: { readonly detail: IncidentDetail; readonly copy: OpsCenterCopy }): ReactNode {
  const [query, setQuery] = useState("");
  const [cohortFilter, setCohortFilter] = useState<CustomerCohortFilter>(CUSTOMER_COHORT_FILTER.All);
  const [tierFilter, setTierFilter] = useState(CUSTOMER_FILTER_ALL);
  const [stateFilter, setStateFilter] = useState(CUSTOMER_FILTER_ALL);
  const [routeFilter, setRouteFilter] = useState(CUSTOMER_FILTER_ALL);
  const [resolutionFilter, setResolutionFilter] = useState(CUSTOMER_FILTER_ALL);
  const summary = detail.communicationSummary;
  const context = customerDrilldownContext(detail);
  const filters = {
    cohort: cohortFilter,
    query: query.trim().toLowerCase(),
    resolution: resolutionFilter,
    route: routeFilter,
    state: stateFilter,
    tier: tierFilter
  };
  const matchingCustomers = detail.customerImpact.filter((customer) => customerMatchesFilters(customer, filters, context, copy));
  const visibleCustomers = matchingCustomers.slice(0, CUSTOMER_DRILLDOWN_LIMIT);
  return (
    <section className="customer-drilldown">
      <SectionHeader
        title={copy.sections.impactedCustomers}
        status={`${summary.impactedCustomerCount} ${copy.labels.impacted}`}
        tone={summary.complaintCount > 0 || summary.deliveryRiskCount > 0 ? UI_TONE.Warn : UI_TONE.Ok}
      />
      <dl className="metric-grid customer-drilldown-metrics">
        <Metric label={copy.labels.impactedCustomers} value={String(summary.impactedCustomerCount)} />
        <Metric label={copy.labels.enterpriseCustomers} value={String(summary.enterpriseCustomerCount)} />
        <Metric label={copy.labels.complaints} value={String(summary.complaintCount)} />
        <Metric label={copy.labels.customerNotice} value={statusLabel(copy, summary.customerNotificationState)} />
        <Metric label={copy.labels.resolutionNotifications} value={statusLabel(copy, summary.resolutionNotificationState)} />
        <Metric label={copy.labels.deliveryRisks} value={String(summary.deliveryRiskCount)} />
      </dl>
      <section className="customer-segment-section">
        <SectionHeader title={copy.labels.customerCohorts} status={String(customerCohortRows(detail, context, copy).length)} tone={UI_TONE.Neutral} />
        <div className="customer-segment-grid">
          {customerCohortRows(detail, context, copy).map((row) => (
            <article className={`customer-segment-card ${row.tone}`} key={row.label}>
              <div>
                <strong>{row.label}</strong>
                <span>{row.value}</span>
              </div>
              <p>{row.summary}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="customer-segment-section">
        <SectionHeader title={copy.labels.customerSegments} status={String(customerSegmentRows(detail, context, copy).length)} tone={UI_TONE.Neutral} />
        <div className="customer-segment-grid">
          {customerSegmentRows(detail, context, copy).map((row) => (
            <article className={`customer-segment-card ${row.tone}`} key={row.label}>
              <div>
                <strong>{row.label}</strong>
                <span>{row.value}</span>
              </div>
              <p>{row.summary}</p>
            </article>
          ))}
        </div>
      </section>
      <details className="customer-drilldown-details">
        <summary>
          <span>{copy.labels.customerDrillIn}</span>
          <StatusPill label={customerVisibleLabel(copy, visibleCustomers.length, matchingCustomers.length)} tone={matchingCustomers.length ? UI_TONE.Neutral : UI_TONE.Warn} />
        </summary>
        <div className="customer-drilldown-filter-grid">
          <TextField label={copy.labels.searchCustomers} name="customerSearch" type="search" value={query} onChange={setQuery} />
          <SelectField label={copy.labels.customerCohort} name="customerCohort" options={customerCohortOptions(copy)} value={cohortFilter} onChange={(value) => setCohortFilter(value as CustomerCohortFilter)} />
          <SelectField label={copy.labels.customerTier} name="customerTier" options={customerTierOptions(detail, copy)} value={tierFilter} onChange={setTierFilter} />
          <SelectField label={copy.labels.customerState} name="customerState" options={customerStateOptions(detail, copy)} value={stateFilter} onChange={setStateFilter} />
          <SelectField label={copy.labels.route} name="customerRoute" options={customerRouteOptions(detail, copy)} value={routeFilter} onChange={setRouteFilter} />
          <SelectField label={copy.labels.resolutionReceipt} name="resolutionReceipt" options={resolutionReceiptOptions(detail, context, copy)} value={resolutionFilter} onChange={setResolutionFilter} />
        </div>
        {visibleCustomers.length ? (
          <>
            <CustomerListRows rows={visibleCustomers.map((customer) => customerDrilldownRow(customer, context, copy))} />
            {matchingCustomers.length > visibleCustomers.length ? (
              <p className="list-overflow-note">{customerVisibleLabel(copy, visibleCustomers.length, matchingCustomers.length)}</p>
            ) : null}
          </>
        ) : (
          <p className="subtle-copy">{copy.queue.noMatches}</p>
        )}
      </details>
    </section>
  );
}

interface CustomerSegmentRow {
  readonly label: string;
  readonly value: string;
  readonly summary: string;
  readonly tone: UiTone;
}

function customerCohortRows(detail: IncidentDetail, context: CustomerDrilldownContext, copy: OpsCenterCopy): readonly CustomerSegmentRow[] {
  const summary = detail.communicationSummary;
  const resolutionSentCount = detail.customerImpact.filter((customer) => resolutionNotificationSent(customerResolutionState(customer, context))).length;
  const deliveryRiskCount = detail.customerImpact.filter((customer) => hasDeliveryRisk(customer, context)).length;
  return [
    {
      label: copy.labels.impactedCustomers,
      value: String(summary.impactedCustomerCount),
      summary: `${summary.enterpriseCustomerCount} ${copy.labels.enterpriseCustomers} / ${countUnique(detail.customerImpact.map((item) => item.affectedRouteRef))} ${copy.labels.routes}`,
      tone: summary.impactedCustomerCount > 0 ? UI_TONE.Warn : UI_TONE.Ok
    },
    {
      label: copy.sections.complainingCustomers,
      value: String(detail.complainingCustomers.length),
      summary: `${summary.complaintCount} ${copy.labels.complaints}`,
      tone: summary.complaintCount > 0 ? UI_TONE.Danger : UI_TONE.Ok
    },
    {
      label: copy.labels.notifiedCustomers,
      value: String(customerNotificationCount(detail)),
      summary: `${copy.labels.customerNotice}: ${statusLabel(copy, context.customerNoticeState)}`,
      tone: notificationSent(context.customerNoticeState) ? UI_TONE.Ok : UI_TONE.Warn
    },
    {
      label: copy.labels.resolutionNotified,
      value: `${resolutionSentCount}/${detail.customerImpact.length}`,
      summary: summarizeCountRows(customerResolutionCounts(detail, context, copy), copy),
      tone: deliveryRiskCount > 0 ? UI_TONE.Warn : UI_TONE.Ok
    },
    {
      label: copy.labels.deliveryRisks,
      value: String(deliveryRiskCount),
      summary: deliveryRiskCount ? copy.labels.deliveryRisk : copy.sections.clear,
      tone: deliveryRiskCount ? UI_TONE.Warn : UI_TONE.Ok
    }
  ];
}

function customerSegmentRows(detail: IncidentDetail, context: CustomerDrilldownContext, copy: OpsCenterCopy): readonly CustomerSegmentRow[] {
  const routeCounts = countValues(detail.customerImpact.map((item) => routeLabel(item.affectedRouteRef, copy)));
  const stateCounts = countValues(detail.customerImpact.map((item) => statusLabel(copy, item.state)));
  const resolutionCounts = customerResolutionCounts(detail, context, copy);
  return [
    {
      label: copy.labels.impactedRoutes,
      value: String(routeCounts.length),
      summary: summarizeCountRows(routeCounts, copy),
      tone: routeCounts.length ? UI_TONE.Warn : UI_TONE.Ok
    },
    {
      label: copy.labels.customerStates,
      value: String(stateCounts.length),
      summary: summarizeCountRows(stateCounts, copy),
      tone: stateCounts.some(([label]) => label !== copy.status.recovered) ? UI_TONE.Warn : UI_TONE.Ok
    },
    {
      label: copy.labels.resolutionReceipt,
      value: String(resolutionCounts.length),
      summary: summarizeCountRows(resolutionCounts, copy),
      tone: detail.communicationSummary.deliveryRiskCount > 0 ? UI_TONE.Warn : UI_TONE.Ok
    }
  ];
}

function customerDrilldownContext(detail: IncidentDetail): CustomerDrilldownContext {
  return {
    complaintRefs: new Set(detail.complainingCustomers.map((item) => item.customerRef).filter((customerRef): customerRef is string => Boolean(customerRef))),
    customerNoticeState: detail.communicationSummary.customerNotificationState,
    resolutionStateByCustomer: new Map(detail.resolutionNotifications.map((receipt) => [receipt.customerRef, receipt.state]))
  };
}

function customerMatchesFilters(
  customer: CustomerImpactRow,
  filters: {
    readonly cohort: CustomerCohortFilter;
    readonly query: string;
    readonly resolution: string;
    readonly route: string;
    readonly state: string;
    readonly tier: string;
  },
  context: CustomerDrilldownContext,
  copy: OpsCenterCopy
): boolean {
  const resolutionState = customerResolutionState(customer, context);
  return customerMatchesCohort(customer, filters.cohort, context)
    && optionMatches(filters.tier, customer.tier)
    && optionMatches(filters.state, customer.state)
    && optionMatches(filters.route, customer.affectedRouteRef)
    && optionMatches(filters.resolution, resolutionState)
    && (!filters.query || customerSearchText(customer, context, copy).includes(filters.query));
}

function customerMatchesCohort(customer: CustomerImpactRow, cohort: CustomerCohortFilter, context: CustomerDrilldownContext): boolean {
  if (cohort === CUSTOMER_COHORT_FILTER.Complaints) {
    return customer.complaintCount > 0 || context.complaintRefs.has(customer.customerRef);
  }
  if (cohort === CUSTOMER_COHORT_FILTER.CustomerNotified) {
    return notificationSent(context.customerNoticeState);
  }
  if (cohort === CUSTOMER_COHORT_FILTER.ResolutionNotified) {
    return resolutionNotificationSent(customerResolutionState(customer, context));
  }
  if (cohort === CUSTOMER_COHORT_FILTER.DeliveryRisk) {
    return hasDeliveryRisk(customer, context);
  }
  return true;
}

function customerDrilldownRow(customer: CustomerImpactRow, context: CustomerDrilldownContext, copy: OpsCenterCopy): readonly [string, string] {
  const complaintLabel = context.complaintRefs.has(customer.customerRef) ? copy.status.linked : copy.labels.none;
  const resolutionState = customerResolutionState(customer, context);
  return [
    customer.displayName,
    [
      statusLabel(copy, customer.tier),
      serviceLabel(customer.affectedServiceRef, copy),
      routeLabel(customer.affectedRouteRef, copy),
      `${copy.labels.customerState}: ${statusLabel(copy, customer.state)}`,
      `${copy.labels.customerNotice}: ${statusLabel(copy, context.customerNoticeState)}`,
      `${copy.labels.complaints}: ${customer.complaintCount}`,
      `${copy.labels.resolutionReceipt}: ${statusLabel(copy, resolutionState)}`,
      `${copy.sections.complainingCustomers}: ${complaintLabel}`
    ].join(" / ")
  ];
}

function customerSearchText(customer: CustomerImpactRow, context: CustomerDrilldownContext, copy: OpsCenterCopy): string {
  return [
    customer.customerRef,
    customer.displayName,
    statusLabel(copy, customer.tier),
    serviceLabel(customer.affectedServiceRef, copy),
    routeLabel(customer.affectedRouteRef, copy),
    statusLabel(copy, customer.state),
    statusLabel(copy, context.customerNoticeState),
    statusLabel(copy, customerResolutionState(customer, context)),
    String(customer.complaintCount)
  ].join(" ").toLowerCase();
}

function CustomerListRows({ rows }: { readonly rows: readonly (readonly [string, string])[] }): ReactNode {
  return (
    <div className="list-rows">
      {rows.map(([label, value]) => (
        <div className="list-row" key={`${label}:${value}`}>
          <strong>{label}</strong>
          <span>{value}</span>
        </div>
      ))}
    </div>
  );
}

function customerCohortOptions(copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [
    [CUSTOMER_COHORT_FILTER.All, copy.labels.allCustomers],
    [CUSTOMER_COHORT_FILTER.Complaints, copy.labels.withComplaints],
    [CUSTOMER_COHORT_FILTER.CustomerNotified, copy.labels.notifiedCustomers],
    [CUSTOMER_COHORT_FILTER.ResolutionNotified, copy.labels.resolutionNotified],
    [CUSTOMER_COHORT_FILTER.DeliveryRisk, copy.labels.withDeliveryRisk]
  ];
}

function customerTierOptions(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [[CUSTOMER_FILTER_ALL, copy.labels.allTiers], ...uniqueOptions(detail.customerImpact.map((customer) => customer.tier), (value) => statusLabel(copy, value))];
}

function customerStateOptions(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [[CUSTOMER_FILTER_ALL, copy.labels.allStates], ...uniqueOptions(detail.customerImpact.map((customer) => customer.state), (value) => statusLabel(copy, value))];
}

function customerRouteOptions(detail: IncidentDetail, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [[CUSTOMER_FILTER_ALL, copy.labels.allRoutes], ...uniqueOptions(detail.customerImpact.map((customer) => customer.affectedRouteRef), (value) => routeLabel(value, copy))];
}

function resolutionReceiptOptions(detail: IncidentDetail, context: CustomerDrilldownContext, copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [
    [CUSTOMER_FILTER_ALL, copy.labels.allResolutionStates],
    ...uniqueOptions(detail.customerImpact.map((customer) => customerResolutionState(customer, context)), (value) => statusLabel(copy, value))
  ];
}

function uniqueOptions(values: readonly string[], labelFor: (value: string) => string): readonly (readonly [string, string])[] {
  return [...new Set(values.filter(Boolean))]
    .sort((left, right) => labelFor(left).localeCompare(labelFor(right)))
    .map((value) => [value, labelFor(value)] as const);
}

function customerResolutionCounts(detail: IncidentDetail, context: CustomerDrilldownContext, copy: OpsCenterCopy): readonly (readonly [string, number])[] {
  return countValues(detail.customerImpact.map((customer) => statusLabel(copy, customerResolutionState(customer, context))));
}

function customerResolutionState(customer: CustomerImpactRow, context: CustomerDrilldownContext): IncidentNotificationState {
  return context.resolutionStateByCustomer.get(customer.customerRef) ?? customer.resolutionNotificationState;
}

function customerNotificationCount(detail: IncidentDetail): number {
  return notificationSent(detail.communicationSummary.customerNotificationState) ? detail.communicationSummary.impactedCustomerCount : 0;
}

function notificationSent(state: IncidentNotificationState): boolean {
  return state === IncidentNotificationState.Sent;
}

function resolutionNotificationSent(state: IncidentNotificationState): boolean {
  return state === IncidentNotificationState.Sent;
}

function hasDeliveryRisk(customer: CustomerImpactRow, context: CustomerDrilldownContext): boolean {
  const state = customerResolutionState(customer, context);
  return state === IncidentNotificationState.ApprovalRequired
    || state === IncidentNotificationState.Failed
    || state === IncidentNotificationState.PartiallyFailed
    || state === IncidentNotificationState.Paused
    || state === IncidentNotificationState.Queued
    || state === IncidentNotificationState.Sending;
}

function optionMatches(filterValue: string, rowValue: string): boolean {
  return filterValue === CUSTOMER_FILTER_ALL || filterValue === rowValue;
}

function customerVisibleLabel(copy: OpsCenterCopy, shown: number, total: number): string {
  return formatLabel(copy.labels.showingCustomers, {
    shown: String(shown),
    total: String(total)
  });
}

function countValues(values: readonly string[]): readonly (readonly [string, number])[] {
  const counts = new Map<string, number>();
  for (const value of values) {
    const key = value || "";
    if (key) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return [...counts.entries()].sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]));
}

function countUnique(values: readonly string[]): number {
  return new Set(values.filter(Boolean)).size;
}

function summarizeCountRows(rows: readonly (readonly [string, number])[], copy: OpsCenterCopy): string {
  return rows.slice(0, 4).map(([label, count]) => `${label}: ${count}`).join(" / ") || copy.empty.noneRecorded;
}
